// SessionReviewModal.js
// Consolidated session review — 3D card grid layout (4-5 per row, chronological).
// Shows complete exercise sequence with inline markers for newly added/removed exercises.

import React, { useState, useEffect, useMemo } from "react";
import api from "./api";

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}
function fmtTime(d) {
  if (!d) return "";
  return new Date(d).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
}
function fmtDuration(secs) {
  if (!secs || secs <= 0) return null;
  const m = Math.floor(secs / 60), s = secs % 60;
  return m > 0 ? `${m}m${s > 0 ? ` ${s}s` : ""}` : `${s}s`;
}
function fmtMinutes(mins) {
  if (!mins || mins <= 0) return null;
  const h = Math.floor(mins / 60), m = mins % 60;
  return h > 0 ? `${h}h${m > 0 ? ` ${m}m` : ""}` : `${m}m`;
}
function sessionDate(s) {
  return new Date(s.start || s.date || s.createdAt);
}
function daysBetween(d1, d2) {
  return Math.round(Math.abs(new Date(d2) - new Date(d1)) / (1000 * 60 * 60 * 24));
}
function groupByMonth(sessions) {
  const groups = {};
  sessions.forEach(s => {
    const key = sessionDate(s).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
    if (!groups[key]) groups[key] = [];
    groups[key].push(s);
  });
  return groups;
}
function changesForSession(session, prevSession, consultHistory) {
  const thisDate = sessionDate(session);
  const prevDate = prevSession ? sessionDate(prevSession) : new Date(0);
  return consultHistory.filter(h => {
    const hd = new Date(h.date);
    return hd > prevDate && hd <= thisDate;
  });
}

const PART_LABELS_MAP = {
  cervical: "Cervical/Neck", upperback: "Upper Back", middleback: "Middle Back",
  lowerback: "Lower Back", chest: "Chest", heart: "Heart", digestive: "Digestive",
  abdomen: "Abdomen", renal: "Renal", reproductive: "Reproductive", coccyx: "Coccyx",
  lung: "Lungs", shoulder_L: "Shoulder (L)", shoulder_R: "Shoulder (R)",
  arm_L: "Arm (L)", arm_R: "Arm (R)", wrist_L: "Wrist (L)", wrist_R: "Wrist (R)",
  knee_L: "Knee (L)", knee_R: "Knee (R)", thigh_L: "Thigh (L)", thigh_R: "Thigh (R)",
  leg_L: "Leg (L)", leg_R: "Leg (R)", calf_L: "Calf (L)", calf_R: "Calf (R)",
  ankle_L: "Ankle (L)", ankle_R: "Ankle (R)", foot_L: "Foot (L)", foot_R: "Foot (R)",
};

function changeIcons(reviews) {
  const added = [], removed = [], modified = [], held = [], recAdded = [], recRemoved = [], partsRemoved = [];
  reviews.forEach(r => {
    const ch = r.changes || {};
    (ch.sequencesAdded    || []).forEach(n => !added.includes(n)         && added.push(n));
    (ch.sequencesRemoved  || []).forEach(n => !removed.includes(n)       && removed.push(n));
    (ch.sequencesModified || []).forEach(n => !modified.includes(n)      && modified.push(n));
    (r.sequences || []).filter(s => s.held).forEach(s => !held.includes(s.name) && held.push(s.name));
    (ch.recommendationsAdded    || []).forEach(n => !recAdded.includes(n)   && recAdded.push(n));
    (ch.recommendationsRemoved  || []).forEach(n => !recRemoved.includes(n) && recRemoved.push(n));
    (ch.partsRemoved || []).forEach(k => {
      const label = PART_LABELS_MAP[k] || k;
      if (!partsRemoved.includes(label)) partsRemoved.push(label);
    });
  });
  return { added, removed, modified, held, recAdded, recRemoved, partsRemoved };
}

function hasAnyChanges(icons) {
  return [icons.added, icons.removed, icons.modified, icons.held,
    icons.recAdded, icons.recRemoved, icons.partsRemoved || []].some(a => a.length > 0);
}

// ── Shared sub-components ─────────────────────────────────────────────────────

const STATUS_META = {
  "session completed": { label: "Done",        color: "#16a34a", bg: "#dcfce7" },
  "Completed":         { label: "Done",        color: "#16a34a", bg: "#dcfce7" },
  "completed":         { label: "Done",        color: "#16a34a", bg: "#dcfce7" },
  "inprogress":        { label: "In Progress", color: "#d97706", bg: "#fef3c7" },
  "Skipped":           { label: "Skipped",     color: "#6b7280", bg: "#f3f4f6" },
  "skipped":           { label: "Skipped",     color: "#6b7280", bg: "#f3f4f6" },
  "pending":           { label: "Pending",     color: "#3b82f6", bg: "#eff6ff" },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || { label: status || "—", color: "#888", bg: "#f3f4f6" };
  return (
    <span style={{ fontSize: 10, fontWeight: 700, color: m.color, background: m.bg,
      borderRadius: 99, padding: "1px 7px" }}>{m.label}</span>
  );
}

function ScoreBar({ label, value, max = 10, trend }) {
  if (value == null) return null;
  const pct   = Math.min(100, (value / max) * 100);
  const color = value >= 7 ? "#dc2626" : value >= 4 ? "#d97706" : "#16a34a";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
      <span style={{ fontSize: 10, color: "#6b7280", width: 70, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
      <div style={{ flex: 1, minWidth: 20, background: "#f3f4f6", borderRadius: 4, height: 6, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, background: color, height: "100%", borderRadius: 4 }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 800, color, minWidth: 18, textAlign: "right", flexShrink: 0 }}>{value}</span>
      {trend != null && trend !== 0 && (
        <span style={{ fontSize: 10, color: trend < 0 ? "#16a34a" : "#dc2626", fontWeight: 700, minWidth: 24, flexShrink: 0 }}>
          {trend < 0 ? `↓${Math.abs(trend)}` : `↑${trend}`}
        </span>
      )}
    </div>
  );
}

/* ── Complete Exercise Sequence Section ─────────────────────────────────── */
/*
 * Shows the FULL exercise list for this session in order.
 * Each exercise is numbered (1, 2, 3...) and shows:
 *   - Status icon: ✓ (done), ⊘ (skipped), · (not started)
 *   - Name
 *   - Time spent
 *   - NEW badge if this exercise was added to the plan between prev and current session
 *   - REMOVED tag if it was removed (shown as strikethrough at end)
 *
 * Example output:
 *   EXERCISES (8)  —  Done: 6 · Skipped: 1 · Not started: 1
 *   ┌─────────────────────────────────────────────────────┐
 *   │ 1  ✓  Neck Flexion                           2m30s │
 *   │ 2  ✓  Shoulder Rolls                         1m15s │
 *   │ 3  ✓  Cat-Cow Stretch              ➕ NEW    3m00s │  ← added between sessions
 *   │ 4  ✓  Forward Bend                           2m00s │
 *   │ 5  ⊘  Bridge Pose                    (skip)    —   │
 *   │ 6  ✓  Knee Extension                         1m45s │
 *   │ 7  ✓  Ankle Rotation               ➕ NEW    1m00s │  ← added between sessions
 *   │ 8  ·  Cool Down Breathing                      —   │
 *   │ ──────────────────────────────────────────────────  │
 *   │    🗑  Wall Squats                   (removed)      │  ← was in prev, not in current
 *   └─────────────────────────────────────────────────────┘
 */
function ExerciseSequenceSection({ snapshot, addedNames, removedNames }) {
  if ((!snapshot || !snapshot.length) && (!removedNames || !removedNames.length)) return null;

  const exercises = snapshot || [];
  const done    = exercises.filter(e => e.status === 'Completed');
  const skipped = exercises.filter(e => e.status === 'Skipped' || e.status === 'skipped');
  const notDone = exercises.filter(e => e.status !== 'Completed' && e.status !== 'Skipped' && e.status !== 'skipped');
  const addedSet = new Set((addedNames || []).map(n => n.toLowerCase()));

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={sStyles.sectionLabel}>
        Exercises ({exercises.length})
        {exercises.length > 0 && (
          <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, marginLeft: 8 }}>
            Done: {done.length}
            {skipped.length > 0 && <span style={{ color: '#d97706' }}> · Skipped: {skipped.length}</span>}
            {notDone.length > 0 && <span style={{ color: '#9ca3af' }}> · Not started: {notDone.length}</span>}
          </span>
        )}
      </div>

      {/* Full sequence table */}
      <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
        {exercises.map((ex, i) => {
          const isCompleted = ex.status === 'Completed';
          const isSkipped   = ex.status === 'Skipped' || ex.status === 'skipped';
          const isNew       = addedSet.has((ex.name || '').toLowerCase());
          const statusColor = isCompleted ? '#16a34a' : isSkipped ? '#d97706' : '#9ca3af';
          const statusIcon  = isCompleted ? '✓' : isSkipped ? '⊘' : '·';

          return (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '5px 10px', fontSize: 12,
              borderBottom: i < exercises.length - 1 ? '1px solid #f3f4f6' : 'none',
              background: isNew ? '#f0fdf4' : 'transparent',
            }}>
              {/* Sequence # */}
              <span style={{ width: 18, textAlign: 'right', color: '#9ca3af', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                {i + 1}
              </span>
              {/* Status icon */}
              <span style={{ width: 14, textAlign: 'center', fontWeight: 700, color: statusColor, flexShrink: 0 }}>
                {statusIcon}
              </span>
              {/* Name */}
              <span style={{ flex: 1, color: isCompleted ? '#374151' : statusColor,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {ex.name}
              </span>
              {/* NEW badge */}
              {isNew && (
                <span style={{ fontSize: 9, fontWeight: 800, color: '#16a34a', background: '#dcfce7',
                  border: '1px solid #bbf7d0', borderRadius: 4, padding: '0 4px', flexShrink: 0 }}>
                  ➕ NEW
                </span>
              )}
              {/* Reps/adjustments */}
              {ex.adjustments && ex.adjustments.length > 0 && (
                <span style={{ fontSize: 9, color: '#7c3aed', flexShrink: 0 }}>
                  ~{ex.adjustments.length}adj
                </span>
              )}
              {/* Time spent */}
              <span style={{ fontSize: 10, color: '#9ca3af', minWidth: 36, textAlign: 'right', flexShrink: 0 }}>
                {ex.timeSpentSec > 0 ? fmtDuration(ex.timeSpentSec) : '—'}
              </span>
            </div>
          );
        })}

        {/* Removed exercises (were in prev session, not in current) */}
        {removedNames && removedNames.length > 0 && (
          <>
            <div style={{ borderTop: '1px dashed #e5e7eb', padding: '4px 10px',
              fontSize: 10, color: '#9ca3af', fontWeight: 600 }}>
              Removed from plan
            </div>
            {removedNames.map((name, i) => (
              <div key={`rm-${i}`} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '4px 10px', fontSize: 12, background: '#fef2f2',
                borderBottom: i < removedNames.length - 1 ? '1px solid #fecaca30' : 'none',
              }}>
                <span style={{ width: 18 }} />
                <span style={{ width: 14, textAlign: 'center', color: '#dc2626', fontWeight: 700 }}>🗑</span>
                <span style={{ flex: 1, color: '#dc2626', textDecoration: 'line-through' }}>{name}</span>
                <span style={{ fontSize: 9, color: '#dc2626', background: '#fee2e2',
                  border: '1px solid #fecaca', borderRadius: 4, padding: '0 4px' }}>
                  REMOVED
                </span>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

/* ── Recommendation Tracking Section ───────────────────────────────────── */
function RecommendationSection({ tracking }) {
  if (!tracking || !tracking.length) return null;
  const doneCount = tracking.filter(r => r.completed).length;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={sStyles.sectionLabel}>Recommendations ({doneCount}/{tracking.length} done)</div>
      <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
        {tracking.map((r, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6,
            padding: '4px 10px', fontSize: 12,
            borderBottom: i < tracking.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
            <span style={{ fontWeight: 700, width: 14, textAlign: 'center',
              color: r.completed ? '#16a34a' : '#dc2626' }}>
              {r.completed ? '✓' : '✗'}
            </span>
            <span style={{ flex: 1, color: r.completed ? '#374151' : '#9ca3af',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {r.name}
            </span>
            {r.qty != null && r.qty > 0 && (
              <span style={{ fontSize: 10, color: '#6b7280' }}>x{r.qty}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── ROM/MVC/Pain change section ───────────────────────────────────────── */
function MetricsChangeSection({ session, prevSession }) {
  const fb     = session.sessionFeedback || {};
  const prevFb = prevSession?.sessionFeedback || {};
  const rows = [];

  const romByPart = fb.romByPart || {};
  const prevRom   = prevFb.romByPart || {};
  Object.entries(romByPart).forEach(([part, val]) => {
    if (val == null) return;
    const prev = prevRom[part];
    const diff = prev != null ? val - prev : null;
    if (diff === 0) return;
    rows.push({ label: `ROM — ${PART_LABELS_MAP[part] || part}`, value: val, diff, type: 'rom' });
  });

  const mvcByPart = fb.mvcByPart || {};
  const prevMvc   = prevFb.mvcByPart || {};
  Object.entries(mvcByPart).forEach(([part, val]) => {
    if (val == null) return;
    const prev = prevMvc[part];
    const diff = prev != null ? val - prev : null;
    if (diff === 0) return;
    rows.push({ label: `MVC — ${PART_LABELS_MAP[part] || part}`, value: val, diff, type: 'mvc' });
  });

  const painByPart = fb.painByPart || {};
  const prevPain   = prevFb.painByPart || {};
  Object.entries(painByPart).forEach(([part, val]) => {
    if (val == null) return;
    const prev = prevPain[part];
    const diff = prev != null ? val - prev : null;
    if (diff === 0) return;
    rows.push({ label: `Pain — ${PART_LABELS_MAP[part] || part}`, value: val, diff, type: 'pain' });
  });

  if (!Object.keys(romByPart).length && fb.rom != null) {
    const diff = prevFb.rom != null ? fb.rom - prevFb.rom : null;
    if (diff !== 0) rows.push({ label: "ROM (overall)", value: fb.rom, diff, type: 'rom' });
  }
  if (!Object.keys(mvcByPart).length && fb.mvc != null) {
    const diff = prevFb.mvc != null ? fb.mvc - prevFb.mvc : null;
    if (diff !== 0) rows.push({ label: "MVC (overall)", value: fb.mvc, diff, type: 'mvc' });
  }

  if (!rows.length) return null;

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={sStyles.sectionLabel}>ROM / MVC / Pain Changes</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 16px' }}>
        {rows.map((r, i) => {
          const goodDir = r.type === 'pain' ? -1 : 1;
          const diffColor = r.diff == null ? '#888'
            : (r.diff * goodDir > 0) ? '#16a34a' : '#dc2626';
          return (
            <div key={i} style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 6,
              padding: '3px 0', borderBottom: '1px solid #f5f5f5' }}>
              <span style={{ flex: 1, color: '#555' }}>{r.label}</span>
              <span style={{ fontWeight: 700, color: '#374151' }}>{r.value}</span>
              {r.diff != null && (
                <span style={{ fontWeight: 700, color: diffColor, fontSize: 10, minWidth: 30 }}>
                  {r.diff > 0 ? `↑${r.diff}` : r.diff < 0 ? `↓${Math.abs(r.diff)}` : '—'}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Feedback scores ─────────────────────────────────────────────────────── */
const SCORE_KEYS = [
  { key: "pain",        label: "Pain" },
  { key: "numbness",    label: "Numbness" },
  { key: "weakness",    label: "Weakness" },
  { key: "stiffness",   label: "Stiffness" },
  { key: "burning",     label: "Burning" },
  { key: "spasm",       label: "Spasm" },
  { key: "instability", label: "Instability" },
  { key: "overallImprovement",  label: "Improvement" },
  { key: "recommendationScore", label: "Rec. Score" },
];

/* ══════════════════════════════════════════════════════════════════════════
   3D SESSION CARD — always expanded, shown in a grid (4-5 per row)
   ══════════════════════════════════════════════════════════════════════════ */
function SessionCard3D({ session, consultChanges, sessionNum, prevSession,
  treatmentStartDate, gapInfo, bills, showPayment }) {

  const d          = sessionDate(session);
  const fb         = session.sessionFeedback || {};
  const ss         = session.sessionState    || {};
  const icons      = changeIcons(consultChanges);
  const hasChanges = hasAnyChanges(icons);
  const duration   = fmtDuration(ss.elapsedSec);
  const therapist  = session.leadTherapist || session.username || "—";
  const hasFeedback = SCORE_KEYS.some(k => fb[k.key] != null);
  const prevFb     = prevSession?.sessionFeedback || {};

  const plannedMins = session.start && session.end
    ? Math.round((new Date(session.end) - new Date(session.start)) / 60000) : null;
  const daysSinceStart = treatmentStartDate ? daysBetween(treatmentStartDate, d) : null;

  const exSnap = fb.exerciseSnapshot || [];
  const recTracking = fb.recommendationTracking || [];

  // Payment on this date
  const sessionDateStr = d.toISOString().slice(0, 10);
  const dayBills = (bills || []).filter(b => {
    const bd = new Date(b.invoicedate || b.createdAt);
    return bd.toISOString().slice(0, 10) === sessionDateStr;
  });
  const dayPayment = dayBills.reduce((sum, b) => sum + (Number(b.paid) || 0), 0);

  const addedNames   = icons.added;
  const removedNames = icons.removed;

  const accentColor = hasChanges ? '#7c3aed' : session.escalated ? '#dc2626' : '#6366f1';

  return (
    <div style={{
      background: '#fff', borderRadius: 14, overflow: 'hidden',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.06)',
      transform: 'perspective(800px) rotateX(1deg)',
      transition: 'transform 0.2s, box-shadow 0.2s',
      display: 'flex', flexDirection: 'column',
      border: `1px solid ${hasChanges ? '#e9d5ff' : '#e5e7eb'}`,
      minWidth: 0,
    }}
    onMouseEnter={e => { e.currentTarget.style.transform = 'perspective(800px) rotateX(0deg) translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.08)'; }}
    onMouseLeave={e => { e.currentTarget.style.transform = 'perspective(800px) rotateX(1deg)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.06)'; }}
    >

      {/* ── Card Header — Session # + Date ── */}
      <div style={{
        background: `linear-gradient(135deg, ${accentColor}, ${accentColor}dd)`,
        padding: '10px 12px', color: '#fff',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
          <span style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.5px' }}>#{sessionNum}</span>
          <StatusBadge status={session.status} />
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, marginTop: 2 }}>{fmtDate(d)}</div>
        <div style={{ fontSize: 10, opacity: 0.85, display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 2 }}>
          {fmtTime(d) && <span>{fmtTime(d)}</span>}
          {duration && <span>⏱ {duration}</span>}
          {daysSinceStart != null && <span>Day {daysSinceStart}</span>}
        </div>
      </div>

      {/* ── Card Body ── */}
      <div style={{ padding: '10px 12px', flex: 1, fontSize: 11, overflow: 'hidden' }}>

        {/* Therapist + badges row */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 10, color: '#6b7280', fontWeight: 600 }}>
            {therapist}
            {session.helperTherapist1 && <span> · {session.helperTherapist1}</span>}
          </span>
          {session.escalated && (
            <span style={{ fontSize: 9, fontWeight: 700, color: '#dc2626',
              background: '#fee2e2', borderRadius: 99, padding: '1px 5px' }}>🚨 Esc</span>
          )}
          {gapInfo && gapInfo.gapDays > 1 && (
            <span style={{ fontSize: 9, color: gapInfo.hadLeave ? '#d97706' : '#9ca3af',
              background: gapInfo.hadLeave ? '#fef3c7' : '#f3f4f6',
              borderRadius: 4, padding: '1px 5px' }}>
              {gapInfo.gapDays}d gap
            </span>
          )}
          {hasChanges && (
            <span style={{ fontSize: 9, fontWeight: 700, color: '#7c3aed',
              background: '#f3e8ff', borderRadius: 99, padding: '1px 5px' }}>Plan changed</span>
          )}
        </div>

        {/* Timing compact */}
        {(session.actualStartTime || session.actualEndTime || plannedMins) && (
          <div style={{ marginBottom: 6 }}>
            <div style={sStyles.sectionLabel}>Timing</div>
            <div style={{ fontSize: 10, color: '#555', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {session.actualStartTime && <span>{fmtTime(session.actualStartTime)}</span>}
              {session.actualEndTime && <span>→ {fmtTime(session.actualEndTime)}</span>}
              {plannedMins > 0 && ss.elapsedSec > 0 && (() => {
                const diff = Math.round(ss.elapsedSec / 60) - plannedMins;
                if (Math.abs(diff) < 2) return null;
                return <span style={{ color: diff > 0 ? '#d97706' : '#3b82f6', fontWeight: 600 }}>
                  ({diff > 0 ? `+${diff}m` : `${Math.abs(diff)}m under`})
                </span>;
              })()}
            </div>
          </div>
        )}

        {/* Exercises — compact list */}
        <ExerciseSequenceSection snapshot={exSnap} addedNames={addedNames} removedNames={removedNames} />

        {/* Recommendations */}
        <RecommendationSection tracking={recTracking} />

        {/* ROM/MVC/Pain */}
        <MetricsChangeSection session={session} prevSession={prevSession} />

        {/* Feedback scores */}
        {hasFeedback && (
          <div style={{ marginBottom: 8 }}>
            <div style={sStyles.sectionLabel}>Feedback</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 8px' }}>
              {SCORE_KEYS.map(({ key, label }) => {
                if (fb[key] == null) return null;
                const prevVal = prevFb[key];
                const trend = prevVal != null ? fb[key] - prevVal : null;
                return <ScoreBar key={key} label={label} value={fb[key]} trend={trend} />;
              })}
            </div>
            {fb.notes && (
              <div style={{ fontSize: 10, color: '#555', fontStyle: 'italic', marginTop: 2 }}>
                {fb.notes}
              </div>
            )}
          </div>
        )}

        {/* Comments */}
        {(session.sessionComments || session.commentsByThe || session.commentsByDr || session.commentsByPt) && (
          <div style={{ marginBottom: 8 }}>
            <div style={sStyles.sectionLabel}>Comments</div>
            {session.sessionComments && <div style={sStyles.commentBox}><span style={sStyles.commentLabel}>S: </span>{session.sessionComments}</div>}
            {session.commentsByThe && <div style={sStyles.commentBox}><span style={sStyles.commentLabel}>T: </span>{session.commentsByThe}</div>}
            {session.commentsByDr && <div style={sStyles.commentBox}><span style={sStyles.commentLabel}>Dr: </span>{session.commentsByDr}</div>}
            {session.commentsByPt && <div style={sStyles.commentBox}><span style={sStyles.commentLabel}>Pt: </span>{session.commentsByPt}</div>}
          </div>
        )}

        {/* Escalation */}
        {session.escalated && (
          <div style={{ marginBottom: 6, padding: '4px 8px', background: '#fef2f2',
            border: '1px solid #fecaca', borderRadius: 6 }}>
            <div style={{ fontSize: 10, color: '#dc2626', fontWeight: 600 }}>
              🚨 Escalated{session.escalationDate && <span style={{ fontWeight: 400 }}> {fmtDate(session.escalationDate)}</span>}
            </div>
          </div>
        )}

        {/* Payment */}
        {showPayment && (dayPayment > 0 || session.visitCharges > 0) && (
          <div style={{ marginBottom: 6 }}>
            <div style={sStyles.sectionLabel}>Payment</div>
            <div style={{ fontSize: 10, color: '#374151' }}>
              {dayPayment > 0 && <span>Paid: <strong style={{ color: '#16a34a' }}>₹{dayPayment.toLocaleString('en-IN')}</strong></span>}
              {session.visitCharges > 0 && <span style={{ marginLeft: dayPayment > 0 ? 8 : 0 }}>Charges: ₹{session.visitCharges.toLocaleString('en-IN')}</span>}
            </div>
          </div>
        )}

        {/* Plan changes */}
        {hasChanges && (
          <div style={{ borderTop: '1px dashed #e5e7eb', paddingTop: 6, marginTop: 4 }}>
            <div style={sStyles.sectionLabel}>Plan Changes</div>
            {consultChanges.map((r, i) => {
              const ch = r.changes || {};
              return (
                <div key={i} style={{ marginBottom: 4, padding: '4px 6px', background: '#faf5ff',
                  border: '1px solid #e9d5ff', borderRadius: 5, fontSize: 10 }}>
                  <div style={{ fontSize: 9, color: '#888', marginBottom: 2 }}>
                    {r.reviewType === 'plan_submitted' ? '📋 Plan' : r.reviewType === 'reset' ? '↩ Reset' : '✏️ Review'}
                    {r.reviewedBy && <span> · {r.reviewedBy}</span>}
                  </div>
                  {ch.sequencesAdded?.length > 0 && <div style={{ color: '#16a34a' }}>+ {ch.sequencesAdded.join(', ')}</div>}
                  {ch.sequencesRemoved?.length > 0 && <div style={{ color: '#dc2626' }}>- {ch.sequencesRemoved.join(', ')}</div>}
                  {ch.sequencesModified?.length > 0 && <div style={{ color: '#d97706' }}>~ {ch.sequencesModified.join(', ')}</div>}
                  {ch.recommendationsAdded?.length > 0 && <div style={{ color: '#0891b2' }}>+Rec: {ch.recommendationsAdded.join(', ')}</div>}
                  {ch.recommendationsRemoved?.length > 0 && <div style={{ color: '#be185d' }}>-Rec: {ch.recommendationsRemoved.join(', ')}</div>}
                </div>
              );
            })}
          </div>
        )}

        {/* Empty state */}
        {!exSnap.length && !recTracking.length && !hasFeedback && !hasChanges &&
          !session.sessionComments && !session.commentsByThe && !session.commentsByDr && (
          <div style={{ fontSize: 10, color: '#aaa', textAlign: 'center', padding: '6px 0' }}>
            No data recorded.
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Modal ────────────────────────────────────────────────────────────────

export default function SessionReviewModal({ patientCard, patientName, sessions,
  treatmentStartDate, onClose }) {
  const [consultHistory,   setConsultHistory]   = useState([]);
  const [bills,            setBills]            = useState([]);
  const [leaveMap,         setLeaveMap]         = useState({});
  const [loading,          setLoading]          = useState(true);
  const [activeMonth,      setActiveMonth]      = useState(null);
  const [showPayment,      setShowPayment]      = useState(false);

  useEffect(() => {
    Promise.all([
      api.get(`${process.env.REACT_APP_URL}/patients/consultation/${patientCard}/review-history`)
        .catch(() => ({ data: [] })),
      api.get(`${process.env.REACT_APP_URL}/bills/${patientCard}`)
        .catch(() => ({ data: [] })),
    ]).then(([histRes, billRes]) => {
      setConsultHistory(histRes.data || []);
      setBills(billRes.data || []);
    }).finally(() => setLoading(false));
  }, [patientCard]);

  // Build leave date map
  useEffect(() => {
    if (!sessions || !sessions.length) return;
    const therapists = [...new Set(sessions.map(s => s.username || s.leadTherapist).filter(Boolean))];
    if (!therapists.length) return;
    Promise.all(therapists.map(t =>
      api.get(`${process.env.REACT_APP_URL}/appointments/type/Leave/${encodeURIComponent(t)}`)
        .catch(() => ({ data: [] }))
    )).then(results => {
      const map = {};
      results.forEach(r => {
        (r.data || []).forEach(leave => {
          if (leave.approved !== 'approved' && leave.approved !== 'applied') return;
          const s = new Date(leave.start || leave.date);
          const e = leave.end ? new Date(leave.end) : s;
          for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
            map[d.toISOString().slice(0, 10)] = true;
          }
        });
      });
      setLeaveMap(map);
    }).catch(() => {});
  }, [sessions]);

  // Chronological order (oldest first) for card grid
  const chronological = useMemo(() =>
    [...(sessions || [])].sort((a, b) => sessionDate(a) - sessionDate(b)),
    [sessions]);
  const totalSessions = chronological.length;

  const monthGroups = groupByMonth(chronological);
  const monthKeys   = Object.keys(monthGroups);

  useEffect(() => {
    if (monthKeys.length > 0 && !activeMonth) setActiveMonth(monthKeys[monthKeys.length - 1]);
  }, [monthKeys.length]); // eslint-disable-line

  function getSessionChanges(session) {
    const idx  = chronological.findIndex(s => s._id === session._id);
    const prev = idx > 0 ? chronological[idx - 1] : null;
    return changesForSession(session, prev, consultHistory);
  }
  function sessionNumber(session) {
    return chronological.findIndex(s => s._id === session._id) + 1;
  }
  function getPrevSession(session) {
    const idx = chronological.findIndex(s => s._id === session._id);
    return idx > 0 ? chronological[idx - 1] : null;
  }
  function getGapInfo(session) {
    const prev = getPrevSession(session);
    if (!prev) return null;
    const d1 = sessionDate(prev);
    const d2 = sessionDate(session);
    const gapDays = daysBetween(d1, d2);
    let hadLeave = false;
    for (let d = new Date(d1); d <= d2; d.setDate(d.getDate() + 1)) {
      if (leaveMap[d.toISOString().slice(0, 10)]) { hadLeave = true; break; }
    }
    return { gapDays, hadLeave };
  }

  const displaySessions = activeMonth ? (monthGroups[activeMonth] || []) : chronological;
  const totalDays = treatmentStartDate ? daysBetween(treatmentStartDate, new Date()) : null;
  const totalWithChanges = chronological.filter(s => {
    const ch = getSessionChanges(s);
    if (!ch.length) return false;
    return hasAnyChanges(changeIcons(ch));
  }).length;

  return (
    <div style={sStyles.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={sStyles.modal}>

        {/* ── Header ── */}
        <div style={sStyles.header}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#1a202c' }}>
              📊 Consolidated Review
            </div>
            <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
              {patientName || patientCard}
              {' · '}<strong>{totalSessions}</strong> sessions
              {totalDays != null && <span style={{ color: '#6b7280' }}> · {totalDays} days</span>}
              {totalWithChanges > 0 && <span style={{ color: '#7c3aed' }}> · {totalWithChanges} plan changes</span>}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginRight: 8 }}>
            <button onClick={() => setShowPayment(p => !p)} style={{
              fontSize: 10, padding: '2px 8px', borderRadius: 99, cursor: 'pointer',
              border: '1px solid #e5e7eb', fontWeight: 600,
              background: showPayment ? '#dcfce7' : '#f9fafb',
              color: showPayment ? '#16a34a' : '#9ca3af',
            }}>
              {showPayment ? '₹ Hide' : '₹ Show'}
            </button>
          </div>
          <button style={sStyles.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* ── Month tabs ── */}
        {monthKeys.length > 1 && (
          <div style={{ display: 'flex', gap: 6, padding: '8px 14px',
            overflowX: 'auto', borderBottom: '1px solid #f0f0f0', flexShrink: 0 }}>
            <button onClick={() => setActiveMonth(null)} style={{
              padding: '4px 12px', borderRadius: 99, fontSize: 12, fontWeight: 600,
              border: '1.5px solid', cursor: 'pointer', whiteSpace: 'nowrap',
              borderColor: !activeMonth ? '#7c3aed' : '#e5e7eb',
              background:  !activeMonth ? '#7c3aed' : '#fff',
              color:       !activeMonth ? '#fff'    : '#555',
            }}>
              All ({totalSessions})
            </button>
            {monthKeys.map(m => (
              <button key={m} onClick={() => setActiveMonth(m)} style={{
                padding: '4px 12px', borderRadius: 99, fontSize: 12, fontWeight: 600,
                border: '1.5px solid', cursor: 'pointer', whiteSpace: 'nowrap',
                borderColor: activeMonth === m ? '#7c3aed' : '#e5e7eb',
                background:  activeMonth === m ? '#7c3aed' : '#fff',
                color:       activeMonth === m ? '#fff'    : '#555',
              }}>
                {m} ({monthGroups[m].length})
              </button>
            ))}
          </div>
        )}

        {/* ── Body — 3D card grid ── */}
        <div style={sStyles.body}>
          {loading && (
            <div style={{ padding: 28, color: '#888', fontSize: 13, textAlign: 'center' }}>
              Loading…
            </div>
          )}

          {!loading && displaySessions.length === 0 && (
            <div style={{ padding: 36, color: '#aaa', fontSize: 13, textAlign: 'center' }}>
              No therapy sessions found.
            </div>
          )}

          {!loading && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: 14,
            }}>
              {displaySessions.map(session => (
                <SessionCard3D
                  key={session._id}
                  session={session}
                  consultChanges={getSessionChanges(session)}
                  sessionNum={sessionNumber(session)}
                  prevSession={getPrevSession(session)}
                  treatmentStartDate={treatmentStartDate}
                  gapInfo={getGapInfo(session)}
                  bills={bills}
                  showPayment={showPayment}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const sStyles = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 3200, padding: 12,
  },
  modal: {
    background: '#fff', borderRadius: 14,
    width: 'min(1400px, 97vw)', maxHeight: '92vh',
    display: 'flex', flexDirection: 'column',
    boxShadow: '0 24px 60px rgba(0,0,0,0.28)',
  },
  header: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
    padding: '14px 18px 12px', borderBottom: '1px solid #e8e8e8', flexShrink: 0,
    gap: 8,
  },
  closeBtn: {
    background: 'none', border: 'none', fontSize: 18, cursor: 'pointer',
    color: '#888', padding: '0 0 0 8px', lineHeight: 1, flexShrink: 0,
  },
  body: {
    flex: 1, overflowY: 'auto', padding: '14px 16px',
  },
  sectionLabel: {
    fontSize: 10, fontWeight: 700, color: '#9ca3af',
    textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6,
  },
  commentBox: {
    fontSize: 12, color: '#374151', fontStyle: 'italic',
    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6,
    padding: '5px 10px', marginBottom: 3,
  },
  commentLabel: {
    fontStyle: 'normal', fontWeight: 700, color: '#6b7280',
  },
};
